package org.teddapp.project.card

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.teddapp.project.db.repo.PreferencesRepository

/**
 * La carte de visite : ce que tu donnes à quelqu'un pour qu'il te retrouve.
 * Tout est modifiable ; un champ vide n'apparaît ni sur la carte ni dans le partage.
 *
 * Elle voyage avec le profil synchronisé, sérialisée dans son champ `bio` : ce champ
 * libre n'était affiché nulle part, et s'en servir évite une migration des trois schémas.
 */
@Serializable
data class BusinessCard(
    val name: String = "",
    val title: String = "",
    val organization: String = "",
    val tagline: String = "",
    val phone: String = "",
    val email: String = "",
    val website: String = "",
    val location: String = "",
    /** Autres liens (LinkedIn, GitHub…), séparés par une virgule ou un retour à la ligne. */
    val links: String = "",
) {
    val linkList: List<String> get() = links.split(',', '\n').map { it.trim() }.filter { it.isNotEmpty() }

    val isEmpty: Boolean
        get() = listOf(name, title, organization, tagline, phone, email, website, location, links).all { it.isBlank() }

    /** « Titre · Organisation », ou ce qui est rempli des deux. */
    val headline: String get() = listOf(title.trim(), organization.trim()).filter { it.isNotEmpty() }.joinToString(" · ")

    /** vCard 3.0 : le format que les téléphones savent ajouter aux contacts. */
    fun toVCard(): String = buildString {
        fun line(key: String, value: String) { if (value.isNotBlank()) append(key).append(':').append(escape(value.trim())).append("\r\n") }
        append("BEGIN:VCARD\r\nVERSION:3.0\r\n")
        line("FN", name)
        // « Nom;Prénoms » : le dernier mot est pris pour le nom de famille.
        if (name.isNotBlank()) name.trim().let { n ->
            append("N:").append(escape(n.substringAfterLast(' '))).append(';').append(escape(n.substringBeforeLast(' ', ""))).append("\r\n")
        }
        line("TITLE", title)
        line("ORG", organization)
        line("TEL;TYPE=CELL", phone)
        line("EMAIL", email)
        line("URL", website)
        linkList.forEach { line("URL", it) }
        line("ADR", location.trim().takeIf { it.isNotEmpty() }?.let { ";;;$it;;;" }.orEmpty())
        line("NOTE", tagline)
        append("END:VCARD\r\n")
    }

    /** La carte en texte simple, pour une messagerie. */
    fun toShareText(): String = buildList {
        if (name.isNotBlank()) add(name.trim())
        if (headline.isNotEmpty()) add(headline)
        if (tagline.isNotBlank()) add(tagline.trim())
        if (size > 0) add("")
        if (phone.isNotBlank()) add("Tél. : ${phone.trim()}")
        if (email.isNotBlank()) add("E-mail : ${email.trim()}")
        if (website.isNotBlank()) add("Site : ${website.trim()}")
        if (location.isNotBlank()) add("Ville : ${location.trim()}")
        addAll(linkList)
    }.joinToString("\n").trim()

    companion object {
        const val PREF_KEY = "business_card"
        private val json = Json { ignoreUnknownKeys = true }

        // Le point-virgule sépare les parties de N et ADR : il n'est pas échappé pour elles.
        private fun escape(value: String): String =
            value.replace("\\", "\\\\").replace("\n", "\\n").replace(",", "\\,")

        /**
         * Lit la carte depuis le champ du profil qui la porte. Un texte qui n'est pas
         * une carte est l'ancienne « bio » : elle devient la phrase d'accroche.
         */
        fun decode(raw: String?): BusinessCard {
            val text = raw?.trim().orEmpty()
            if (text.isEmpty()) return BusinessCard()
            return runCatching { json.decodeFromString(serializer(), text) }.getOrNull() ?: BusinessCard(tagline = text)
        }

        fun encode(card: BusinessCard): String = json.encodeToString(serializer(), card)

        /** Carte enregistrée sur l'appareil par la première version de l'écran, avant la synchronisation. */
        suspend fun loadLegacy(prefs: PreferencesRepository): BusinessCard =
            prefs.get(PREF_KEY)?.let { decode(it) } ?: BusinessCard()
    }
}

/**
 * Partage natif, fourni par la plateforme qui en a un (le sélecteur d'applications
 * d'Android). Absent, l'écran copie la carte dans le presse-papiers.
 */
object TeddShare {
    var shareText: ((subject: String, text: String) -> Unit)? = null

    /**
     * Partage ou enregistre une image PNG. Rend le message à afficher, ou `null` si
     * la plateforme a pris la main (ou si l'utilisateur a annulé).
     */
    var shareImage: (suspend (fileName: String, image: androidx.compose.ui.graphics.ImageBitmap) -> String?)? = null

    /** Vrai si [shareImage] enregistre un fichier au lieu d'ouvrir un sélecteur d'applications. */
    var imageIsSaved: Boolean = false
}
